import React from 'react';
import cn from 'classnames';

import { Tooltip } from '@components/common';
import UserSpinner from '@components/common/UserSpinner';

import { useQueryApprovalInfo } from '@hooks/assets/useQueryApprovalInfo';
import { useQueryUser } from '@hooks/useQueryUser';

import { IAssetRentRequestCardApproveItem, IAssetRentRequestCardItem } from '@interfaces/rent/assetRent';
import { IShortUserInfo } from '@interfaces/user/user';

import { ERequestItemApprovalStatuses } from '@enums/asset';

import { getCorrectTooltipForApprovalBtn } from './utils';

import styles from './RequestTable.module.scss';

export interface IApprovalsButtonProps {
	isEmptyApprovalTooltipDisabled: boolean;
	asset: IAssetRentRequestCardItem;
	isAO: boolean;
	isNoApprovalUnits: boolean;
	openApprovalInfo: () => void;
	totalApprovingUnits: number;
	isAssetItemsLoading?: boolean;
	isAssetShownOnMap?: boolean;
}

export function ApprovalsButton({
	isEmptyApprovalTooltipDisabled,
	asset,
	isAO,
	isNoApprovalUnits,
	openApprovalInfo,
	totalApprovingUnits,
	isAssetItemsLoading,
	isAssetShownOnMap,
}: IApprovalsButtonProps) {
	const { user } = useQueryUser();

	const { approvalInfoItems, isLoadingApprovalInfo } = useQueryApprovalInfo({
		assetId: asset.id,
		isAO,
		searchValue: '',
		isAssetItemsLoading,
		isAssetShownOnMap,
	});

	const isIncludedInApprovalUnits = approvalInfoItems.some((item: IAssetRentRequestCardApproveItem) => {
		return item?.approvalUnit?.users?.some((approvalUnitUser: IShortUserInfo) => {
			return approvalUnitUser?.id === user?.id;
		});
	});

	const classnames = cn(styles.approved, {
		[styles.refused]: asset.approveStatus.name
		=== ERequestItemApprovalStatuses.disapproved,
		[styles.fullyApproved]: asset.approveStatus.name
		=== ERequestItemApprovalStatuses.approved,
		[styles.asMNO]: isAO ? !isIncludedInApprovalUnits : true,
		[styles.disabled]: isNoApprovalUnits || !isAssetShownOnMap,
	});

	const tooltip = getCorrectTooltipForApprovalBtn({
		asset,
		userId: user?.id,
		isIncludedInApprovalUnits,
	});

	return isLoadingApprovalInfo
		? (
			<UserSpinner size={16} />
		)
		: (
			<Tooltip
				content={tooltip}
				isDisabled={!tooltip || isEmptyApprovalTooltipDisabled}
			>
				<div
					className={classnames}
					onClick={openApprovalInfo}
				>
					<span>Approvals&nbsp;</span>
					<span>
					{
						asset.disapproved
							? `${asset.approved}/${asset.disapproved}/${totalApprovingUnits}`
							: `${asset.approved}/${totalApprovingUnits}`
					}
				</span>
				</div>
			</Tooltip>
		);
}
